import { usePhoneNumberRegister } from "@becon-team/becon-fe-lib/hooks";
import { BottomSheetModal, useModal } from "@becon-team/becon-fe-lib/my-modal";
import { toaster } from "@becon-team/becon-fe-lib/my-toast";
import { cn } from "@becon-team/becon-fe-lib/my-tailwind-css";
import { beconCountryDialCodes, countryInfos } from "@becon-team/becon-fe-lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { TextInput } from "components/@common/TextInput/TextInput";
import { useInput } from "hooks/@common/useInput";
import { modalIds } from "libs/my-modal";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { validateNum } from "utils/@validate/utils_validate";
import { z } from "zod";
import { usePostCheckVerificationCode, usePostPhoneVerificationCode } from "../services/phone.query";
import { PhoneNumberFormInput } from "@becon-team/becon-fe-lib/components";
import { BeconCommonTypes } from "@becon-team/becon-fe-lib/my-types";
import { formatBeconPhoneNumberInsert } from "../(beconCountryDialCode)";

export const PhoneVerificationModal = () => {
  const FormSchema = z.object({
    phoneNumber: z.string(),
    countryDialCode: z.string(),
  });
  const formatCountryNumber = (countryNumber: string) => {
    return countryNumber.split("[")[1].replace("]", "");
  };

  const modalId = modalIds["phone-verification"];
  const { t } = useTranslation();
  const { modal, closeModal } = useModal(modalId);
  const { isOpened } = modal;

  const { register, watch } = useForm<z.infer<typeof FormSchema>>({
    mode: "onSubmit",
    resolver: zodResolver(FormSchema),
    defaultValues: {
      countryDialCode:
        beconCountryDialCodes.find((beconCountryDialCode) => beconCountryDialCode.includes("🇰🇷")) || "+82",
    },
  });

  const phoneNumberRegister = usePhoneNumberRegister(register("phoneNumber"));
  const countryDialCodeRegister = register("countryDialCode");

  const {
    attribute: verificationCodeInputAttribute,
    value: verificationCodeInputValue,
    isValidSubmit: isValidSubmitVerificationCode,
  } = useInput({
    initialValue: "",
    placeholder: t("인증번호를 입력해주세요."),
    submitValidator: (password) => {
      return password.length === 5;
    },
    inputValidator: validateNum,
    inputAttribute: {
      maxLength: 5,
      autoComplete: "one-time-code",
    },
  });

  const currentFormState = watch();

  const { phoneNumber, countryDialCode } = currentFormState;

  const {
    mutate: postPhoneVerificationCode,
    isPending: isPendingPhoneVerificationCode,
    isSuccess: isSuccessPhoneVerificationCode,
  } = usePostPhoneVerificationCode();

  const {
    mutate: postCheckVerificationCode,
    isPending: isPendingCheckVerificationCode,
    isSuccess: isSuccessCheckVerificationCode,
  } = usePostCheckVerificationCode();

  const handleSendVerificationCodeToPhone = () => {
    const normalizedPhoneNumber = formatBeconPhoneNumberInsert({
      countryDialCode,
      phoneNumber,
    });

    postPhoneVerificationCode(
      {
        phoneNumber: normalizedPhoneNumber,
      },
      {
        onSuccess: () => {
          toaster.success(t("인증번호를 전송했어요!"));
        },
      },
    );
  };

  const handleVerifyCode = () => {
    const formattedCountryNumber = formatCountryNumber(countryDialCode);
    const region: BeconCommonTypes.Region =
      Object.values(countryInfos).find((country) => country.dialCode === formattedCountryNumber)?.code ?? "KR";

    postCheckVerificationCode(
      {
        code: verificationCodeInputValue,
        region,
      },
      {
        onSuccess: () => {
          toaster.success(t("번호가 정상적으로 등록 되었어요"));
          closeModal();
        },
      },
    );
  };

  return (
    <BottomSheetModal
      titleSectionClass="hidden opacity-0"
      closeModal={() => {
        console.log("없애기불가");
      }}
      isOpened={isOpened}
      isCloseModalOutsideClick={false}
      btnSection={
        <button
          form={modalId}
          className={cn("my-btn-primary w-full", { "my-loading": isPendingCheckVerificationCode })}
          disabled={!isSuccessPhoneVerificationCode || !isValidSubmitVerificationCode}
          onClick={() => {
            handleVerifyCode();
          }}
        >
          {t("인증 확인")}
        </button>
      }
    >
      <div className="p-inner-padding pb-0">
        <div className="flex flex-col justify-center items-center ">
          <div className="flex items-center mt-sp-12 gap-sp-8">
            <div className="text-md font-bold text-center">{t("휴대폰 번호를 입력해주세요")}</div>
            <div className="text-sm text-youtube-red font-medium">*{t("필수")}</div>
          </div>

          <div className="bg-yellow-50 rounded-md text-sm p-inner-padding mt-sp-12 tracking-tighter">
            <div>{t("안녕하세요, withbecon을 이용해 주셔서 감사합니다")}</div>
            <div>{t("안전하고 편리한 서비스를 제공하기 위해 휴대폰 번호 인증을 필수로 도입하게 되었어요")}</div>
          </div>
        </div>
        <div className="mt-sp-24">
          <div className="text-sm">{t("휴대폰 번호")}</div>
          <div className="flex items-center gap-sp-6">
            <PhoneNumberFormInput
              countryDialCodes={beconCountryDialCodes}
              countryDialCodeResisterProps={countryDialCodeRegister}
              phoneNumberResisterProps={phoneNumberRegister}
              disabled={false}
            />
            <button
              className={cn("my-btn-primary whitespace-pre", { "my-loading": isPendingPhoneVerificationCode })}
              disabled={!phoneNumber || isSuccessPhoneVerificationCode}
              onClick={() => {
                handleSendVerificationCodeToPhone();
              }}
            >
              {t("인증요청")}
            </button>
          </div>
        </div>
        <div className="mt-sp-12">
          <div className="text-sm">{t("인증번호")}</div>
          <TextInput
            attribute={verificationCodeInputAttribute}
            isValidSubmit={isValidSubmitVerificationCode}
            isDisabled={isSuccessCheckVerificationCode}
          />
        </div>
      </div>
    </BottomSheetModal>
  );
};

export const usePhoneVerificationModal = () => {
  const { modal, openModal, closeModal } = useModal(modalIds["phone-verification"]);

  const openModalCallback = useCallback(() => {
    openModal();
  }, [openModal]);

  return {
    openModal: openModalCallback,
    closeModal,
    isOpened: modal.isOpened,
  };
};
